import { useMemo, useState } from "react";
import { achColor, achievement, isOverdue, parseISO, toISO, todayISO, useStore } from "../store";
import { useTick } from "./Problems";
import { Card, Empty, Field, Select, Table, Td, inputCls } from "../ui";

const MAX_DAYS = 31;

const getRange = (period, cs, ce) => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  if (period === "Daily") return [t, t];
  if (period === "Weekly") {
    const s = new Date(t);
    s.setDate(t.getDate() - ((t.getDay() + 6) % 7));
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return [s, e];
  }
  if (period === "Monthly") return [new Date(t.getFullYear(), t.getMonth(), 1), new Date(t.getFullYear(), t.getMonth() + 1, 0)];
  if (!cs || !ce) return null;
  return [parseISO(cs), parseISO(ce)];
};

const listDays = (s, e) => {
  const out = [];
  for (const d = new Date(s); d <= e && out.length < MAX_DAYS; d.setDate(d.getDate() + 1)) out.push(toISO(d));
  return out;
};

const avg = (list) => {
  const v = list.map(achievement).filter((x) => x != null);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

const useDashboard = (days) => {
  const { people, tasks, problems, meetings, settings } = useStore();
  return useMemo(() => {
    const today = todayISO();
    const inRange = (d) => days.length && d >= days[0] && d <= days[days.length - 1];
    const counted = days.filter((d) => d <= today);
    const rTasks = tasks.filter((t) => inRange(t.date));
    const rows = people.filter((p) => p.status === "Active").map((p) => {
      const mine = rTasks.filter((t) => t.personId === p.id);
      const perDay = Object.fromEntries(days.map((d) => [d, mine.filter((t) => t.date === d).length]));
      const assigned = counted.filter((d) => perDay[d] > 0).length;
      return { p, perDay, assigned, notAssigned: counted.length - assigned, tasks: mine.length, done: mine.filter((t) => t.status === "Done").length, avg: avg(mine) };
    });
    const rProb = problems.filter((p) => inRange(p.datetime.slice(0, 10)));
    const overdue = (p) => isOverdue(p, settings.problemHours);
    const byPriority = ["High", "Medium", "Normal"].map((pr) => {
      const l = rProb.filter((p) => p.priority === pr);
      return { pr, raised: l.length, open: l.filter((p) => p.status === "Open").length, overdue: l.filter(overdue).length };
    });
    const rMeet = meetings.filter((m) => inRange(m.date));
    const byDept = Object.entries(rMeet.reduce((a, m) => ({ ...a, [m.department]: (a[m.department] || 0) + 1 }), {})).sort((a, b) => b[1] - a[1]);
    const kpis = [
      ["Active People", rows.length], ["Tasks Assigned", rTasks.length],
      ["Done", rTasks.filter((t) => t.status === "Done").length], ["Pending", rTasks.filter((t) => t.status !== "Done").length],
      ["Days Not Assigned", rows.reduce((a, r) => a + r.notAssigned, 0), "warn"],
      ["Problems Open", rProb.filter((p) => p.status === "Open").length], ["Problems Overdue", rProb.filter(overdue).length, "warn"],
      ["Meetings Held", rMeet.length],
    ];
    return { rows, kpis, byPriority, byDept, today };
  }, [days, people, tasks, problems, meetings, settings]);
};

const Kpi = ({ label, value, warn, i }) => (
  <Card className="animate-rise p-4 sm:p-5" style={{ animationDelay: `${i * 40}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">{label}</p>
    <p className={`mt-2 font-mono-nums text-3xl font-bold ${warn && value > 0 ? "text-rose-400" : "text-zinc-50"}`}>{value}</p>
  </Card>
);

const dayLabel = (d) => parseISO(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

const GridRow = ({ r, days, today }) => (
  <tr className="transition-colors hover:bg-white/[0.02]" data-testid={`grid-row-${r.p.id}`}>
    <Td className="sticky left-0 z-10 whitespace-nowrap bg-[#111418]">
      <p className="font-medium text-zinc-100">{r.p.name}</p>
      <p className="text-xs text-zinc-500">{r.p.role} · {r.p.department}</p>
    </Td>
    <Td className="text-center font-mono-nums">{r.assigned}</Td>
    <Td className={`text-center font-mono-nums ${r.notAssigned ? "text-rose-400" : ""}`}>{r.notAssigned}</Td>
    <Td className="text-center font-mono-nums">{r.tasks}</Td>
    <Td className="text-center font-mono-nums">{r.done}</Td>
    <Td className={`text-center font-mono-nums font-bold ${achColor(r.avg)}`}>{r.avg == null ? "—" : `${Math.round(r.avg)}%`}</Td>
    {days.map((d) => (
      <Td key={d} className="text-center font-mono-nums" data-testid={`grid-cell-${r.p.id}-${d}`}>
        {r.perDay[d] > 0 ? <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#c6f432]/15 font-bold text-[#c6f432]">{r.perDay[d]}</span>
          : d > today ? <span className="text-zinc-700">·</span>
          : <span className="font-bold text-rose-500">✘</span>}
      </Td>
    ))}
  </tr>
);

const MiniTable = ({ title, head, children, empty, testid }) => (
  <Card data-testid={testid}>
    <h3 className="border-b border-white/[0.07] px-5 py-4 font-heading text-base font-semibold text-zinc-100 md:text-lg">{title}</h3>
    {empty ? <Empty title="Nothing in this period" /> : <Table head={head}>{children}</Table>}
  </Card>
);

const PeriodControls = ({ period, setPeriod, cs, setCs, ce, setCe }) => (
  <div className="flex flex-wrap items-end gap-3" data-testid="period-controls">
    <Field label="Period" className="w-40"><Select options={["Daily", "Weekly", "Monthly", "Custom"]} value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="period-select" /></Field>
    {period === "Custom" && (
      <>
        <Field label="Start date"><input type="date" className={inputCls} value={cs} onChange={(e) => setCs(e.target.value)} data-testid="custom-start-input" /></Field>
        <Field label="End date"><input type="date" className={inputCls} value={ce} onChange={(e) => setCe(e.target.value)} data-testid="custom-end-input" /></Field>
      </>
    )}
  </div>
);

export default function Dashboard() {
  useTick();
  const [period, setPeriod] = useState("Weekly");
  const [cs, setCs] = useState(todayISO());
  const [ce, setCe] = useState(todayISO());
  const range = getRange(period, cs, ce);
  const rangeError = period === "Custom" && range && range[0] > range[1] ? "End date must be on or after start date" : null;
  const tooLong = range && !rangeError && (range[1] - range[0]) / 86400000 + 1 > MAX_DAYS;
  const days = useMemo(() => (range && !rangeError ? listDays(range[0], range[1]) : []), [range?.[0]?.getTime(), range?.[1]?.getTime(), rangeError]); // eslint-disable-line react-hooks/exhaustive-deps
  const { rows, kpis, byPriority, byDept, today } = useDashboard(days);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between animate-rise">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="range-label">
            {days.length ? `${dayLabel(days[0])} – ${dayLabel(days[days.length - 1])} · counted up to today` : "Select a valid period"}
          </p>
        </div>
        <PeriodControls {...{ period, setPeriod, cs, setCs, ce, setCe }} />
      </div>
      {rangeError && <p className="text-sm text-rose-400" data-testid="range-error">{rangeError}</p>}
      {tooLong && <p className="text-sm text-amber-300" data-testid="range-capped">Grid shows the first {MAX_DAYS} days of the selected range.</p>}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {kpis.map(([l, v, w], i) => <Kpi key={l} label={l} value={v} warn={w} i={i} />)}
      </div>

      <Card className="animate-rise [animation-delay:200ms]" data-testid="assignment-grid">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.07] px-5 py-4">
          <h2 className="font-heading text-base font-semibold text-zinc-100 md:text-lg">Daily Assignment Grid</h2>
          <p className="text-xs text-zinc-500"><span className="font-bold text-[#c6f432]">n</span> = tasks assigned · <span className="font-bold text-rose-500">✘</span> = not assigned · <span className="text-zinc-600">·</span> = upcoming</p>
        </div>
        {rangeError ? <Empty testid="grid-invalid" title="Fix the date range to see the grid" /> : rows.length ? (
          <Table head={["Person", "Days Assigned", "Days NOT Assigned", "Tasks", "Done", "Avg Achievement", ...days.map(dayLabel)]}>
            {rows.map((r) => <GridRow key={r.p.id} r={r} days={days} today={today} />)}
          </Table>
        ) : <Empty testid="grid-empty" title="No active people" hint="Add your team in Settings to see the assignment grid." />}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <MiniTable title="Problems by Priority" head={["Priority", "Raised", "Open", "Overdue"]} testid="problems-by-priority">
          {byPriority.map((b) => (
            <tr key={b.pr}><Td className="font-medium text-zinc-100">{b.pr}</Td><Td className="font-mono-nums">{b.raised}</Td><Td className="font-mono-nums">{b.open}</Td><Td className={`font-mono-nums ${b.overdue ? "font-bold text-rose-400" : ""}`}>{b.overdue}</Td></tr>
          ))}
        </MiniTable>
        <MiniTable title="Meetings by Department" head={["Department", "Meetings"]} empty={!byDept.length} testid="meetings-by-department">
          {byDept.map(([d, n]) => <tr key={d}><Td className="font-medium text-zinc-100">{d}</Td><Td className="font-mono-nums">{n}</Td></tr>)}
        </MiniTable>
      </div>
    </div>
  );
}
